import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./UserManagement.scss";
import {
  MotifButton,
  MotifFormField,
  MotifLabel,
  MotifOption,
  MotifSelect,
  MotifTable,
  MotifErrorMessage,
  MotifToast,
  MotifModal,
  MotifModalFooter,
  MotifModalBody,
  MotifProgressLoader,
} from "@ey-xd/motif-react";
import { useForm, Controller } from "react-hook-form";
import useColumnDefinitions from "@/features/usermanagement/components/TableDetails/colHeaderConst";
import useCustomCellRenderers from "@/features/usermanagement/components/TableDetails/customCellConst";
import { roleType } from "@/features/usermanagement/components/userTypes";
import {
  useGetUsersListMutation,
  useCreateUserMutation,
  useGetListUsersQuery,
  useDeleteUserMutation,
  useUpdateRoleMutation,
} from "@/services/userManagement/userReducers";
import {
  createType,
  deleteUserType,
  modalRoleType,
  modalType,
  toastType,
  updateRoleType,
  userSearchType,
} from "./UsermanagementTypes";
import CustomPagination from "@/components/Pagination/CustomPagination";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
  tableRowHeight,
} from "@/components/Table/CustomPaginationConstants";
import SearchInput from "@/components/SearchInput/SearchInput";
import {
  ADD_BUTTON_VARIANT,
  EXPORT_BUTTON_VARIANT,
  NO_BUTTON_VARIANT,
  YES_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import { useDispatch, useSelector } from "react-redux";
import { useTrans } from "@/components/Functions/dateFunc";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
import { useLazyExportDocumentQuery } from "@/services/exports/exportsReducer";
import { commonFormateName } from "@/utility/common";
import { HEADER } from "@/appConstants";

const UserManagement = () => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const retainState = useSelector(
    (e: any) => e.retainState.userManagementStateTab
  );
  const [exportDocument, { isFetching: exportUsersLoading }] =
    useLazyExportDocumentQuery();

  let rolesList: roleType[] = [
    { roleName: "pages.userManagementPage.basicUser", roleId: "0" },
    { roleName: "pages.userManagementPage.caseCreator", roleId: "1" },
    { roleName: "pages.userManagementPage.systemAdmin", roleId: "2" },
  ];
  const commonErrorMessage = "global.globalNotifications.wentWrong";
  let tableLoadingData = customTableLoadingData;
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;

  let timeOutVal: any = undefined;
  const { User: UserPermission } = useSelector(
    (state: any) => state.appPermissions
  );
  const isUserEditPermission =
    UserPermission && UserPermission.includes("Write");
  const isUserDeletePermission =
    UserPermission && UserPermission.includes("Delete");

  const cellRenderers = useCustomCellRenderers({
    handleDelete,
    isUserEditPermission,
    isUserDeletePermission,
  });
  const [
    getUsersList,
    {
      data: serachUsersData,
      isLoading: loadingUserList,
      isError: errorUserList,
      error: searchUserError,
    },
  ] = useGetUsersListMutation();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [retainState]);
  const paginationObj = getPaginationObj;
  const searchUserErrorDetails: any = searchUserError;
  const dispatch = useDispatch();
  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "userManagementStateTab",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }

  const [createUser, { isLoading: loadingCreateUser }] =
    useCreateUserMutation();

  const {
    refetch,
    data: listOfUsers,
    isLoading: loadingUsers,
    isError: errorUsers,
    isFetching: fetchingUsers,
    status: statusUsers,
  } = useGetListUsersQuery(paginationObj);
  const {
    refetch: reftechNewUsers,
    data: listOfNewUsers,
    isLoading: loadingNewUsers,
    isError: errorNewUsers,
    isFetching: fetchingNewUsers,
  } = useGetListUsersQuery({
    PageNumber: 1,
    PageSize: 10,
    SearchText: "",
    SortOrder: "desc",
    SortColumn: "LastUpdatedDate",
  });
  let listOfRecentUserIds: any = [];

  if (
    Array.isArray(listOfNewUsers?.results) &&
    listOfNewUsers?.results.length > 0
  ) {
    listOfRecentUserIds = listOfNewUsers.results.map(
      (user: any) => user.userId
    );
  }
  const columnDefs = useColumnDefinitions(listOfRecentUserIds);

  let userGroupList: any = useMemo(
    () => (listOfUsers ? JSON.parse(JSON.stringify(listOfUsers)) : ""),
    [listOfUsers]
  );

  const [deleteUser, { isLoading: loadingDeleteUser }] =
    useDeleteUserMutation();
  const [UpdateRole, { isLoading: loadingUpdateRoleUser }] =
    useUpdateRoleMutation();
  const modalJson: modalType = {
    show: false,
    userId: "",
    oid: "",
    displayName: "",
  };
  const modalRoleJson: modalRoleType = {
    show: false,
    data: "",
    rowNo: "",
    roleDescription: "",
  };
  const [modalData, setModalData] = useState<modalType>({ ...modalJson });
  const [roleUpdateModal, setRoleUpdateModal] = useState<modalRoleType>({
    ...modalRoleJson,
  });
  const abortFunRef = useRef<any>();
  const showLoadingMessage = useRef<boolean>(false);
  const [quickSearch, setQuickSearch] = useState<string>(
    retainState.SearchText || ""
  );
  const { control, handleSubmit, setValue, getValues, formState, reset } =
    useForm<userSearchType>({
      defaultValues: {
        enteredName: "",
        roleId: "",
        oid: "",
      },
    });

  const toastJson: toastType = {
    showToast: false,
    showMessage: "",
    toastType: "",
  };
  const [showSuccessMessage, setShowSuccessMessage] = useState<toastType>({
    ...toastJson,
  });
  const [optionsListAll, setOptionsListAll] = useState<any>([]);

  function getOptions(optionsData: any) {
    let userEnteredValue: any = getValues("enteredName");
    let userNames: any[] = [];
    if (userEnteredValue && optionsData) {
      userNames = optionsData.map((ele: any) => {
        let optionJson = {
          searchTerm: `${ele.displayName} | ${ele.email}`,
          oid: ele.oid,
        };
        return optionJson;
      });
    }
    setOptionsListAll([...userNames]);
  }

  async function userSearch(enteredValue: string, searchTime: number) {
    if (enteredValue && enteredValue.length < 3) {
      return;
    }

    showLoadingMessage.current = true;
    setOptionsListAll([]);

    try {
      const data = getUsersList({
        searchText: enteredValue,
        limit: 0,
      });
      abortFunRef.current = data;

      let postData: any = await data;
      getOptions(postData?.data);
      showLoadingMessage.current = false;
    } catch {
      showLoadingMessage.current = false;
    }
  }

  function removeToast() {
    clearTimeout(timeOutVal);
    timeOutVal = setTimeout(() => {
      setShowSuccessMessage({ ...toastJson });
    }, 5000);
  }

  const getSystemTimeZone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      return "UTC";
    }
  };

  function userDelete() {
    let userDeleteJson: deleteUserType = {
      id: modalData.userId,
      oid: modalData.oid,
    };
    deleteUser(userDeleteJson).then((value: any) => {
      if (value.data) {
        refetch();
        reftechNewUsers();

        setShowSuccessMessage({
          showToast: true,
          showMessage: "global.globalNotifications.userDeletedMessage",
          toastType: "success",
        });
      } else {
        setShowSuccessMessage({
          showToast: true,
          showMessage: commonErrorMessage,
          toastType: "error",
        });
      }
      setModalData({ ...modalJson });

      removeToast();
    });
  }

  function handleDelete(
    modalShow: boolean,
    userId: string,
    oid: string,
    displayName: string
  ) {
    setModalData({
      show: modalShow,
      userId: userId,
      oid: oid,
      displayName: displayName,
    });
  }
  async function onSubmit(data: any) {
    let userSlectedDetails: userSearchType = { ...data };
    let displayName = userSlectedDetails.enteredName
      .toString()
      .split(" | ")[0]
      .trim();

    let userData: createType = {
      displayName: displayName,
      oid: userSlectedDetails.oid,
      applicationRole: +userSlectedDetails.roleId,
    };
    await createUser(userData)
      .then((value: any) => {
        let messageJson: toastType = { ...toastJson };
        if (value?.error) {
          messageJson = {
            showToast: true,
            showMessage: value?.error?.data?.message
              ? value.error.data.message
              : commonErrorMessage,
            toastType: "error",
          };
        } else {
          messageJson = {
            showToast: true,
            showMessage: value.data.message
              ? value.data.message
              : "pages.userManagementPage.userCreateMessage",
            toastType: "success",
          };
          setOptionsListAll([]);
          refetch();
          reftechNewUsers();
          reset();
        }

        setShowSuccessMessage({ ...messageJson });
        removeToast();
      })
      .catch(() => {
        setShowSuccessMessage({
          showToast: true,
          showMessage: commonErrorMessage,
          toastType: "error",
        });
        removeToast();
      });
  }
  function handleInviteUserErrorMsg() {
    if (loadingUserList || showLoadingMessage.current)
      return (
        <MotifLabel>
          {t("global.globalNotifications.loadingMessage")}
        </MotifLabel>
      );
    else if (errorUserList && searchUserErrorDetails?.name !== "AbortError")
      return <MotifErrorMessage>{t(commonErrorMessage)}</MotifErrorMessage>;
    else if (
      serachUsersData &&
      serachUsersData.length === 0 &&
      getValues("enteredName")
    )
      return (
        <MotifErrorMessage>
          {t("global.globalNotifications.noUserMatchMessage")}
        </MotifErrorMessage>
      );
    else if (formState && formState.errors.oid)
      return (
        <MotifErrorMessage>
          {t(`${formState.errors.oid.message}`)}
        </MotifErrorMessage>
      );
    return <></>;
  }

  function handleSelectSearch(enterdvalue: any) {
    setValue("enteredName", enterdvalue?.searchTerm);
  }
  function handleChangeSearch(enterdvalue: any) {
    enterdvalue = enterdvalue?.split(" | ")[0]?.trimStart();

    if (abortFunRef && abortFunRef.current) {
      abortFunRef.current.abort();
    }
    setValue("enteredName", enterdvalue);
    setValue("oid", "");
    userSearch(enterdvalue, 400);
  }
  function handleUpdateRole(rowData: any, newValue: number, oldValue: string) {
    let roleUpdateJson: updateRoleType = {
      applicationRole: newValue,
      displayName: rowData.displayName,
      oid: rowData.oid,
      userId: rowData.userId,
    };
    let modalJson = {
      show: true,
      data: roleUpdateJson,
      rowNo: roleUpdateModal.rowNo,
      roleDescription: oldValue,
    };
    setRoleUpdateModal({ ...modalJson });
  }
  function updatingUserRole() {
    UpdateRole(roleUpdateModal.data)
      .then((value: any) => {
        if (value?.error) {
          handleRevertRole();

          setShowSuccessMessage({
            showToast: true,
            showMessage: commonErrorMessage,
            toastType: "error",
          });
          removeToast();
        } else {
          refetch();
          setShowSuccessMessage({
            showToast: true,
            showMessage: "pages.userManagementPage.updatedRoleMessage",
            toastType: "success",
          });
          removeToast();
        }
        setRoleUpdateModal({ ...modalRoleJson });
      })
      .catch(() => {
        handleRevertRole();

        setShowSuccessMessage({
          showToast: true,
          showMessage: commonErrorMessage,
          toastType: "error",
        });
        removeToast();
      });
  }
  let defaultval = {
    flex: 1,
    minWidth: 110,
    editable: true,
    cellStyle: {
      whiteSpace: "normal",
      lineHeight: "1.5",
    },
  };
  function handleMotifSorting(e: any, paginationObj: any) {
    if (e?.source !== "gridOptionsChanged") {
      let sortedColumnsLength: any = e?.columns ? e.columns.length : "";
      if (sortedColumnsLength !== "") {
        let coumnName = e?.columns?.[sortedColumnsLength - 1]?.colId;
        let sortName = e?.columns?.[sortedColumnsLength - 1]?.sort;
        let typeName = e?.columns?.[sortedColumnsLength - 1]?.colDef?.type;
        let paginationJson: any = { ...paginationObj };
        let columnTypesCustom: any = {};
        if (typeName) {
          columnTypesCustom[typeName] = { sort: sortName };
        }
        paginationJson.SortOrder = sortName;
        paginationJson.SortColumn =
          coumnName?.charAt(0)?.toUpperCase() + coumnName?.slice(1);
        if (!sortName) {
          delete paginationJson.SortOrder;
          delete paginationJson.SortColumn;
        }
        if (e?.columns[0]?.colDef?.field) {
          if (handlePaginationData) {
            handlePaginationData(paginationJson, columnTypesCustom);
          }
        }
      }
    }
  }
  function resetSortStatus(defaultSort: string) {
    let json: any = { ...retainState.sortedColumnTypes };
    for (const key in json) {
      json[key] = { sort: null };
    }
    json[defaultSort] = { sort: "asc" };

    return json;
  }
  function handleRevertRole() {
    let roleData = userGroupList;
    roleData.results[roleUpdateModal.rowNo].applicationRole =
      roleUpdateModal.roleDescription;
    userGroupList = roleData;
  }
  const listOfUsersData = userGroupList?.results ? userGroupList?.results : [];

  useEffect(() => {
    return () => {
      clearTimeout(timeOutVal);
    };
  }, [timeOutVal]);

  return (
    <div className="ccwp_usermanagment_wrapper">
      <header className="ccwp_usermanagment_header">
        <h1 className="motif-h3-default-bold">
          {t("pages.userManagementPage.title")}{" "}
        </h1>
        <p className="motif-body2-default-light">
          {t("pages.userManagementPage.greetings")}
        </p>
      </header>
      <main>
        {showSuccessMessage.showToast && (
          <MotifToast
            onClose={() => setShowSuccessMessage({ ...toastJson })}
            variant={showSuccessMessage.toastType}
          >
            {t(showSuccessMessage.showMessage)}
          </MotifToast>
        )}

        <div className="formContainer mar-t-15">
          <div className="inviteCon">
            <MotifLabel>{t("pages.userManagementPage.invite")}</MotifLabel>
            <MotifFormField className="searchCon">
              {/*@ts-ignore */}
              <Controller
                control={control}
                rules={{
                  required: "pages.userManagementPage.userInviteError",
                }}
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <SearchInput
                      aria-label={t(
                        "Search for Team Member within App Directory"
                      )}
                      items={optionsListAll}
                      value={getValues("enteredName") || ""}
                      open={!loadingUserList}
                      hideClearButton={false}
                      placeholder={t(
                        "pages.userManagementPage.searchUserPlaceholder"
                      )}
                      onChange={(e: any) => {
                        handleChangeSearch(e.target.value);
                        onChange(e);
                      }}
                      filterPropFunction={(e: any) => {
                        return e?.searchTerm;
                      }}
                      onSelect={(value: any) => {
                        handleSelectSearch(value);
                        return onChange(value?.oid);
                      }}
                    />
                  );
                }}
                name="oid"
              />
            </MotifFormField>

            <MotifErrorMessage className="error-message-con">
              {handleInviteUserErrorMsg()}
            </MotifErrorMessage>
          </div>
          <div className="roleCon">
            <div className="select-role-con">
              <MotifLabel>{t("pages.userManagementPage.role")}</MotifLabel>
              <MotifFormField>
                <Controller
                  control={control}
                  rules={{
                    required: "pages.userManagementPage.rolePlaceholder",
                  }}
                  render={({ field: { onChange, onBlur, value } }) => {
                    return (
                      <MotifSelect
                        aria-label={t("Select Role")}
                        placeholder={t(
                          "pages.userManagementPage.rolePlaceholder"
                        )}
                        visibleOptions={3}
                        value={value}
                        onChange={onChange}
                        triggerButtonProps={{ type: "button" }}
                      >
                        {rolesList.map((option) => (
                          <MotifOption
                            key={option.roleId}
                            value={option.roleId}
                          >
                            {t(option.roleName)}
                          </MotifOption>
                        ))}
                      </MotifSelect>
                    );
                  }}
                  name="roleId"
                />
              </MotifFormField>
              <MotifErrorMessage className="error-message-con">
                {formState && formState.errors.roleId
                  ? t(`${formState.errors.roleId.message}`)
                  : ""}
              </MotifErrorMessage>
            </div>
            <div className="mar-t-20">
              {isUserEditPermission && (
                <MotifButton
                  type="submit"
                  size="medium"
                  loading={loadingCreateUser}
                  variant={ADD_BUTTON_VARIANT}
                  onClick={handleSubmit(onSubmit)}
                >
                  {t("pages.userManagementPage.addUserButton")}
                </MotifButton>
              )}
              {/* Button for Bulk Upload, Commented for later usage */}
              {/* <MotifButton
                  type="button"
                  size="medium"
                  variant="secondary"
                  className="mar-r-15"
                >
                  {t("pages.userManagementPage.bulkUploadButton")}
                </MotifButton> */}
              <MotifErrorMessage className="error-message-con">
                {""}
              </MotifErrorMessage>
            </div>
          </div>
        </div>
        <div
          style={
            !userGroupList ||
            userGroupList?.results?.length === 0 ||
            fetchingUsers
              ? { height: "180px" }
              : {}
          }
          className="ccwp_usermanagment_tablecon"
        >
          {loadingUsers || loadingNewUsers ? (
            <MotifProgressLoader size="xs"></MotifProgressLoader>
          ) : (
            <>
              <MotifTable
                ref={gridRef.current}
                defaultColDef={defaultval}
                suppressMultiSort={true}
                paginateChildRows={true}
                columnDefs={columnDefs}
                components={cellRenderers}
                columnTypes={{ ...retainState.sortedColumnTypes }}
                onSortChanged={(e: any) => {
                  handleMotifSorting(e, paginationObj);
                }}
                titleComplement={
                  <div className="display-flex justify-content-end align-items-center">
                    <MotifFormField>
                      <SearchInput
                        value={quickSearch}
                        onChange={(e: any) => {
                          setQuickSearch(e.target.value);
                        }}
                        onEnter={() => {
                          dispatch(
                            setComponentState1({
                              entityName: "userManagementStateTab",
                              entityState: {
                                PageNumber: 1,
                                SearchText: quickSearch,
                                SortOrder: "asc",
                                SortColumn: "ApplicationRole",
                                PageSize: paginationPageSize,
                                sortedColumnTypes: {
                                  ...resetSortStatus("applicationRoleType"),
                                },
                              },
                            })
                          );
                        }}
                        onClear={() => {
                          setQuickSearch("");
                        }}
                        placeholder={t(
                          "global.globalPlaceholder.quickSearchPlaceHolder"
                        )}
                        withOutDebounce={true}
                      />
                    </MotifFormField>
                    &nbsp; &nbsp;
                    <MotifButton
                      loading={exportUsersLoading}
                      onClick={() =>
                        exportDocument({
                          url: "User/ExportUser",
                          ...paginationObj,
                          fileName: commonFormateName(
                            HEADER.applicationNameInHeader,
                            "UsersList",
                            getSystemTimeZone()
                          ),
                        })
                          .then((response) => {
                            if (response?.error && "data" in response.error) {
                              try {
                                const errObj = JSON.parse(
                                  response?.error?.data as string
                                );
                                setShowSuccessMessage({
                                  showToast: true,
                                  showMessage: errObj?.message || errObj.title,
                                  toastType: "error",
                                });
                              } catch (error) {
                                setShowSuccessMessage({
                                  showToast: true,
                                  showMessage: "Something went wrong!!",
                                  toastType: "error",
                                });
                              }
                            }
                          })
                          .catch((error: any) => {
                            setShowSuccessMessage({
                              showToast: true,
                              showMessage: "Something went wrong!!",
                              toastType: "error",
                            });
                          })
                      }
                      style={{ height: "44px" }}
                      variant={EXPORT_BUTTON_VARIANT}
                    >
                      {t("global.globalActions.export")}
                    </MotifButton>
                  </div>
                }
                rowHeight={tableRowHeight}
                paginationAutoPageSize={false}
                paginationPageSize={paginationPageSize}
                paginationPageSizeSelector={paginationPageSizeSelector}
                rowData={fetchingUsers ? [] : listOfUsersData}
                title={t("pages.userManagementPage.tableTitle")}
                description={t("pages.userManagementPage.tableDescription")}
                overlayNoRowsTemplate={
                  fetchingUsers || fetchingNewUsers
                    ? t(tableLoadingData)
                    : errorUsers || errorNewUsers
                    ? t("global.globalNotifications.wentWrong")
                    : t("global.globalNotifications.noRecords")
                }
                onCellEditingStarted={(e) => {
                  setRoleUpdateModal({
                    show: false,
                    data: "",
                    rowNo: e.rowIndex,
                    roleDescription: "",
                  });
                }}
                onCellEditingStopped={(e) => {
                  if (e.valueChanged) {
                    let rowData = e.data;
                    let changedvalue = e.newValue;
                    let oldValu = e.oldValue;
                    handleUpdateRole(rowData, changedvalue, oldValu);
                  }
                }}
              />
              <CustomPagination
                apiData={userGroupList}
                apiStatus={statusUsers}
                currentpage={paginationObj.PageNumber}
                pageSize={paginationObj.PageSize}
                disableNext={!userGroupList?.hasMoreRecords}
                onChangePageSize={(page: number) => {
                  dispatch(
                    setComponentState1({
                      entityName: "userManagementStateTab",
                      entityState: {
                        PageSize: page,
                        PageNumber: 1,
                      },
                    })
                  );
                }}
                onChangeNextPage={() => {
                  dispatch(
                    setComponentState1({
                      entityName: "userManagementStateTab",
                      entityState: {
                        PageNumber: paginationObj.PageNumber + 1,
                      },
                    })
                  );
                }}
                onChangePreviousPage={() => {
                  dispatch(
                    setComponentState1({
                      entityName: "userManagementStateTab",
                      entityState: {
                        PageNumber: paginationObj.PageNumber - 1,
                      },
                    })
                  );
                }}
                pageSizeSelector={paginationPageSizeSelector}
              />
            </>
          )}
        </div>
      </main>
      <MotifModal
        onClose={() => setModalData({ ...modalJson })}
        show={modalData.show}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
        size="sm"
      >
        <div className="confirmationModalWrapper creditor_disabling_model">
          <br></br>

          <MotifModalBody style={{ paddingTop: "0px" }}>
            <span style={{ fontWeight: "300" }}>
              {useTrans(
                t,
                t("pages.userManagementPage.userDeleteMessage", {
                  userName: modalData.displayName,
                }),
                "userName"
              )}
            </span>
          </MotifModalBody>
          <MotifModalFooter>
            <MotifButton
              onClick={() => userDelete()}
              loading={loadingDeleteUser}
              variant={YES_BUTTON_VARIANT}
            >
              {t("global.globalActions.yes")}
            </MotifButton>

            <MotifButton
              className=""
              onClick={() => setModalData({ ...modalJson })}
              size="small"
              type="button"
              variant={NO_BUTTON_VARIANT}
            >
              {t("global.globalActions.no")}
            </MotifButton>
          </MotifModalFooter>
        </div>
      </MotifModal>
      <MotifModal
        onClose={() => setRoleUpdateModal({ ...modalRoleJson })}
        show={roleUpdateModal.show}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
        size="sm"
      >
        <div className="confirmationModalWrapper creditor_disabling_model">
          <br></br>

          <MotifModalBody style={{ paddingTop: "0px" }}>
            <span style={{ fontWeight: "300" }}>
              {t("global.globalNotifications.roleChangeMessage")}
            </span>
          </MotifModalBody>
          <MotifModalFooter>
            <MotifButton
              onClick={() => updatingUserRole()}
              loading={loadingUpdateRoleUser}
              variant={YES_BUTTON_VARIANT}
            >
              {t("global.globalActions.yes")}
            </MotifButton>

            <MotifButton
              className=""
              onClick={() => {
                handleRevertRole();
                setRoleUpdateModal({ ...modalRoleJson });
              }}
              size="small"
              type="button"
              variant={NO_BUTTON_VARIANT}
            >
              {t("global.globalActions.no")}
            </MotifButton>
          </MotifModalFooter>
        </div>
      </MotifModal>
    </div>
  );
};
export default UserManagement;
